import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DatabaseZap, FileText, GitCompareArrows } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { fetchSuggestionsCached } from '../services/suggestions';

const WelcomeScreen = () => {
  const { theme, setInputDraft, token, isUploadModalOpen } = useAppContext();
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const wasUploadModalOpen = useRef(isUploadModalOpen);

  const iconMap = useMemo(
    () => ({
      FileText,
      GitCompareArrows,
      DatabaseZap,
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const loadSuggestions = async () => {
      const shouldRefresh = wasUploadModalOpen.current && !isUploadModalOpen;
      wasUploadModalOpen.current = isUploadModalOpen;
      if (!token || isUploadModalOpen) {
        setLoadingSuggestions(false);
        return;
      }
      setLoadingSuggestions(true);
      try {
        const response = await fetchSuggestionsCached(token, 6, { forceRefresh: shouldRefresh });
        if (cancelled) return;
        if (Array.isArray(response?.suggestions) && response.suggestions.length > 0) {
          setSuggestions(response.suggestions.slice(0, 6));
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        console.error('Failed to load suggestions:', err);
        if (!cancelled) {
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSuggestions(false);
        }
      }
    };

    loadSuggestions();
    return () => {
      cancelled = true;
    };
  }, [token, isUploadModalOpen]);

  return (
    <div className="text-center h-full flex flex-col justify-center items-center pt-20">
      <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
        Hi, there
      </h1>
      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
        Tell us what you need, and we'll handle the rest.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl mb-8">
        {loadingSuggestions
          ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border animate-pulse`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} h-6 w-6 rounded-full`} />
                  <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} h-4 w-32 rounded`} />
                </div>
                <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} h-3 w-full rounded`} />
                <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} h-3 w-4/5 rounded mt-2`} />
              </div>
            ))
          : suggestions.map((s, i) => {
              const IconComponent = iconMap[s.icon] || DatabaseZap;
              return (
                <div 
                  key={i} 
                  onClick={() => setInputDraft(s.prompt)}
                  className={`${theme === 'dark' ? 'bg-gray-800 border-gray-400' : 'bg-white border-gray-600'} p-4 rounded-lg border cursor-pointer hover:-translate-y-1 transition-transform duration-200`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <IconComponent size={24} className={s.iconColor || 'text-purple-500'} />
                    <h3 className={`font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                      {s.title}
                    </h3>
                  </div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    {s.displayPrompt || s.prompt}
                  </p>
                </div>
              );
            })}
      </div>
    </div>
  );
};

export default WelcomeScreen;
